/**
 * ANI Integration Types
 *
 * Centralized type definitions for ANI swap functionality.
 * Includes types for quotes, callbacks, and UI components.
 */

import { Currency, CurrencyAmount, Percent, Token } from '@uniswap/sdk-core'
import { Route as V3Route } from '@uniswap/v3-sdk'
import Decimal from 'decimal.js'

import { TradeState } from '../state/routing/types'

/**
 * Global parameters read from the ANI contract
 */
export interface AniGlobalParams {
  burnRate: Decimal
  stepSize: Decimal
  price: Decimal
  stepCount: Decimal
  initialPrice: Decimal
  previousPrice: Decimal
  previousStepCount: Decimal
  tokenSupply: Decimal
  contractUSDBalance: Decimal
  // Oracle rate: hyUSD to USDQ conversion (1e18 scale)
  // e.g., if hyUSD is worth $1.17, rate = 1.17e18
  oracleRate: Decimal
}

/**
 * ANI swap information for confirmation modal display
 */
export interface AniSwapInfo {
  inputAmount: CurrencyAmount<Currency>
  outputAmount: CurrencyAmount<Currency>
  inputCurrency: Currency
  outputCurrency: Currency
}

/**
 * Result of ANI quote calculation
 */
export interface AniQuoteResult {
  // Calculated amounts
  aniAmount: CurrencyAmount<Token> | undefined
  hyUsdAmount: CurrencyAmount<Token> | undefined
  tokenAmount: CurrencyAmount<Currency> | undefined
  usdqAmount: string | undefined // USDQ amount in wei (for handler threshold)

  // State
  state: TradeState

  // Direction info
  direction: 'buy' | 'sell' | null
  isDirectSwap: boolean // true if hyUSD <-> ANI (no DEX leg)
  quotedFromInput: boolean // true if quote calculated from input amount (determines DEX swap type)

  // DEX route for multi-hop swaps (extracted from DEX trade)
  dexRoute: V3Route<Currency, Currency> | undefined

  // For display
  priceImpact: Decimal | undefined
  executionPrice: Decimal | undefined
}

/**
 * State of the ANI swap callback
 */
export enum AniSwapCallbackState {
  INVALID,
  LOADING,
  VALID,
}

/**
 * Parameters for ANI swap execution
 */
export interface AniSwapParams {
  aniQuote: AniQuoteResult
  inputCurrency: Currency | null | undefined
  outputCurrency: Currency | null | undefined
  slippageTolerance: Percent
  recipient: string | null
}
